import React, { useState, useEffect } from "react";
import {
Drawer,
Descriptions,
Button,
Tooltip,
Input,
Form,
Spin,
Select,
Modal,
} from "antd";
import {
CheckCircleOutlined,
CloseCircleOutlined,
CheckOutlined,
CloseOutlined,
ExclamationCircleOutlined,
FileTextOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { getRole, getIsApprover } from "../../helpers";
import { 
useGetApprovalRequestsOptionsQuery,
useApproveApprovalRequestMutation,
useRejectApprovalRequestMutation,
useNeedsRevisionApprovalRequestMutation,
useSubmitApprovalRequestMutation,
} from "../../appRedux/api/approvalRequestsApi";
import "./ViewApprovalRequests.css";

const { Option } = Select;
const { TextArea } = Input;
const confirm = Modal.confirm;

const ViewApprovalRequests = ({
    visible,
    onClose,
    requestData,
    isLoading,
    refetchData,
    }) => {
    const [isAdminApprover, setIsAdminApprover] = useState(false);
    const [isUserAdmin, setIsUserAdmin] = useState(false);
    const [isUserPartner, setIsUserPartner] = useState(false);
    const [isUserLegal, setIsUserLegal] = useState(false);
    const [isUserLicensor, setIsUserLicensor] = useState(false);
    
    // API Mutations
    const [approveApprovalRequest, { isLoading: isApproving }] = useApproveApprovalRequestMutation();
    const [rejectApprovalRequest, { isLoading: isRejecting }] = useRejectApprovalRequestMutation();
    const [needsRevisionApprovalRequest, { isLoading: isRevising }] = useNeedsRevisionApprovalRequestMutation();
    const [submitApprovalRequest, { isLoading: isSubmitting }] = useSubmitApprovalRequestMutation();
    
    const [form] = Form.useForm();
    const currentDate = new Date();

    // Fetch approval request options
    const { data: approvalOptions, isLoading: isLoadingOptions } = useGetApprovalRequestsOptionsQuery();

    useEffect(() => {
        setIsAdminApprover(getIsApprover());
        setIsUserAdmin(getRole() === "ADMIN");
        setIsUserPartner(getRole() === "PARTNER");
        setIsUserLegal(getRole() === "LEGAL");
        setIsUserLicensor(getRole() === "LICENSOR");
    }, []);

    // Function to get filename from file path - Same as main component
    const getFileNameFromPath = (filePath) => {
        if (!filePath) return "Document";
        // Decode URI encoded characters and get the filename
        try {
            const decodedPath = decodeURIComponent(filePath);
            return decodedPath.split('/').pop() || "Document";
        } catch (error) {
            return filePath.split('/').pop() || "Document";
        }
    };

    // Conditions for button visibility
    // 1️⃣ The user must be an admin
    // 2️⃣ Case A: Partner revision is NOT required, and the request status is either "PENDING" or "NEEDS REVISION"
            // This means: if no partner review is needed and the request is still pending
            // or has been sent back for revision, the admin can see the Submit button.
    // 3️⃣ Exclusion condition:
        // The button should NOT be shown if any of these are true:
        //   - legalStatus is "APPROVED" (the request is already legally approved)
        //   - legalStatus is "UNDER PROCESS" (legal processing is ongoing)
        //   - licensorStatus is "APPROVED" (the licensor has already approved the request)
        // This ensures that once a request has moved past the admin’s stage,
        // the Submit button is hidden to prevent redundant or invalid submissions.
    const showSubmitButton = () => {
        return isUserAdmin && 
                ((!requestData?.isPartnerRevisionRequired && (requestData?.status === "PENDING" || requestData?.status === "NEEDS REVISION")) || 
                (requestData?.status === "NEEDS REVISION" && requestData?.workflowType !== null && !requestData?.isPartnerRevisionRequired)) && 
                !(requestData?.legalStatus === "APPROVED" || requestData?.licensorStatus === "APPROVED");
    };

    const showNeedsRevisionButton = () => {
        return (isUserAdmin && !requestData?.isPartnerRevisionRequired && (requestData?.status === "PENDING" || requestData?.status === "NEEDS REVISION")) ||
                (isUserLegal && requestData?.legalStatus === "PENDING") ||
                (isUserLicensor && requestData?.licensorStatus === "PENDING");
    };

    const showApproveButton = () => {
        return (isUserAdmin && 
                requestData?.status !== "NEEDS REVISION" &&
                !requestData?.isPartnerRevisionRequired && 
                (requestData?.status === "PENDING" || requestData?.status === "NEEDS REVISION")) || (isUserLegal && requestData?.legalStatus === "PENDING") ||
                (isUserLicensor && requestData?.licensorStatus === "PENDING");
    };

    const showRejectButton = () => {
        return (isUserAdmin && 
                requestData?.status !== "NEEDS REVISION" &&
                requestData?.status !== "REJECTED" &&
                !requestData?.isPartnerRevisionRequired && 
                (requestData?.status === "PENDING" || requestData?.status === "NEEDS REVISION")) || (isUserLegal && requestData?.legalStatus === "PENDING") ||
                (isUserLicensor && requestData?.licensorStatus === "PENDING");
    };

    const canUserAct = () => {
        return showNeedsRevisionButton();
    };

    const showCommentsForm = () => {
        return (
        <Form form={form} layout="vertical" className="mt-20">
            <div className="mb-10">
            <span>Add comments (optional):</span>
            </div>
            <Form.Item
            name="comment"
            className="mb-0"
            >
            <TextArea placeholder="Enter comments here (optional)" />
            </Form.Item>
        </Form>
        );
    };

    const showAdminSubmitForm = () => {
        // Check if workflowType is not null to hide the Request Approval Flow field
        const showWorkflowTypeField = !requestData?.workflowType;
        
        return (
        <Form form={form} layout="vertical" className="mt-20">
            {showWorkflowTypeField && (
                <Form.Item
                name="workflow_type"
                label="Request Approval Flow"
                rules={[
                    {
                    required: true,
                    message: "Please select request approval flow",
                    },
                ]}
                >
                <Select 
                    placeholder="Select Request Flow"
                    loading={isLoadingOptions}
                >
                    {approvalOptions?.workflow_type?.map((option) => (
                    <Option key={option.value} value={option.value}>
                        {option.label}
                    </Option>
                    ))}
                </Select>
                </Form.Item>
            )}
            <Form.Item
            name="comment"
            label="Comments (optional)"
            >
            <TextArea placeholder="Please enter your comments (optional)" rows={4} />
            </Form.Item>
        </Form>
        );
    };

    // Helper function to create FormData from object - only include non-empty values
    const createFormData = (data) => {
        const formData = new FormData();
        Object.keys(data).forEach(key => {
            // Only append if value is not empty, not undefined, and not null
            if (data[key] !== undefined && data[key] !== null && data[key] !== '') {
                formData.append(key, data[key]);
            }
        });
        return formData;
    };

    // Handle admin submit confirmation
    const handleAdminSubmit = () => {
        const approvalRequestId = requestData?.approval_request_id;
        if (!approvalRequestId) {
        message.error("Approval request ID not found");
        return;
        }

        confirm({
        title: "Submit Approval Request",
        className: "btn-confirm-primary",
        content: showAdminSubmitForm(),
        okText: "Submit",
        cancelText: "Cancel",
        icon: <CheckCircleOutlined />,
        onOk(close) {
            form.validateFields().then((values) => {
            // Prepare data object - only include non-empty values
            const requestData = {};
            
            // Only include workflow_type if it's present and not empty
            if (values.workflow_type) {
                requestData.workflow_type = values.workflow_type;
            }
            
            // Only include comment if it's present and not empty
            if (values.comment && values.comment.trim() !== '') {
                requestData.comment = values.comment;
            }
            
            const formData = createFormData(requestData);
            
            submitApprovalRequest({
                id: approvalRequestId,
                data: formData
            }).then((response) => {
                if (!response.error) {
                if (refetchData) refetchData();
                onClose();
                }
            });
            close();
            });
        },
        onCancel() {
            form.resetFields(["workflow_type", "comment"]);
        },
        });
    };

    const handleApprove = () => {
        const approvalRequestId = requestData?.approval_request_id;
        if (!approvalRequestId) {
        message.error("Approval request ID not found");
        return;
        }

        confirm({
        title: "Do you want to approve this request?",
        className: "btn-confirm-green",
        content: showCommentsForm(),
        icon: <CheckCircleOutlined />,
        okText: "Approve",
        cancelText: "Cancel",
        onOk(close) {
            form.validateFields().then((values) => {
            // Prepare data object - only include non-empty comment
            const requestData = {};
            
            // Only include comment if it's present and not empty
            if (values.comment && values.comment.trim() !== '') {
                requestData.comment = values.comment;
            }
            
            const formData = createFormData(requestData);
            
            approveApprovalRequest({
                id: approvalRequestId,
                data: formData
            }).then((response) => {
                if (!response.error) {
                if (refetchData) refetchData();
                onClose();
                }
            });
            close();
            });
        },
        onCancel() {
            form.resetFields(["comment"]);
        },
        });
    };

    const showRejectForm = () => {
        return (
        <Form form={form} layout="vertical" className="mt-20">
            <div className="mb-10">
            <span>Add comments (optional):</span>
            </div>
            <Form.Item
            name="comment"
            className="mb-10"
            >
            <TextArea placeholder="Enter comments here (optional)" />
            </Form.Item>
        </Form>
        );
    };

    const handleReject = () => {
        const approvalRequestId = requestData?.approval_request_id;
        if (!approvalRequestId) {
        message.error("Approval request ID not found");
        return;
        }

        form.resetFields(['comment']);

        confirm({
        title: "Do you want to reject this request?",
        className: "btn-confirm-red",
        content: showRejectForm(),
        okText: "Reject",
        cancelText: "Cancel",
        icon: <CloseCircleOutlined />,
        onOk(close) {
            form.validateFields().then(values => {
            // Prepare data object - only include non-empty comment
            const requestData = {};
            
            // Only include comment if it's present and not empty
            if (values.comment && values.comment.trim() !== '') {
                requestData.comment = values.comment;
            }
            
            const formData = createFormData(requestData);
            
            rejectApprovalRequest({
                id: approvalRequestId,
                data: formData
            }).then((response) => {
                if (!response.error) {
                if (refetchData) refetchData();
                onClose();
                }
            });
            close();
            });
        },
        onCancel() {
            form.resetFields();
        },
        });
    };

    const handleNeedsRevision = () => {
        const approvalRequestId = requestData?.approval_request_id;
        if (!approvalRequestId) {
        message.error("Approval request ID not found");
        return;
        }

        confirm({
        title: "Do you want to request for revisions?",
        className: "btn-confirm-primary",
        content: showCommentsForm(),
        okText: "Submit",
        cancelText: "Cancel",
        icon: <ExclamationCircleOutlined />,
        onOk(close) {
            form.validateFields().then((values) => {
            // Prepare data object - only include non-empty comment
            const requestData = {};
            
            // Only include comment if it's present and not empty
            if (values.comment && values.comment.trim() !== '') {
                requestData.comment = values.comment;
            }
            
            const formData = createFormData(requestData);
            
            needsRevisionApprovalRequest({
                id: approvalRequestId,
                data: formData
            }).then((response) => {
                if (!response.error) {
                if (refetchData) refetchData();
                onClose();
                }
            });
            close();
            });
        },
        onCancel() {
            form.resetFields(["comment"]);
        },
        });
    };

    const renderAttachments = () => {
        // Use documents array instead of attachments - matching the main component
        if (!requestData?.documents || !Array.isArray(requestData.documents) || requestData.documents.length === 0) {
            return <span style={{ color: '#999' }}>No attachments</span>;
        }

        return (
            <div style={{ textAlign: "left" }}>
                {requestData.documents.map((doc, index) => (
                    <div key={doc.document_id || index} style={{ marginBottom: 8, display: 'flex', alignItems: 'center' }}>
                        <FileTextOutlined style={{ marginRight: 8, color: '#283A8A' }} />
                        <a
                            href={doc.file}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ 
                                textDecoration: 'none',
                                color: '#283A8A',
                                fontSize: '14px'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            Doc {index + 1}
                        </a>
                    </div>
                ))}
            </div>
        );
    };

    const labelStyle = { width: "220px", fontWeight: "600" };

    // Get the approval request ID for button IDs
    const approvalRequestId = requestData?.approval_request_id;

    // Check if any action is in progress
    const isActionInProgress = isApproving || isRejecting || isRevising || isSubmitting;

    return (
        <Drawer
        title={`Approval Request - Details`}
        placement="right"
        closable={true}
        onClose={onClose}
        open={visible}
        width="100%"
        className="approval-requests-drawer"
        headerStyle={{ paddingBottom: 16 }}
        bodyStyle={{ paddingTop: 0, overflow: "hidden" }}
        >
        {(isLoading || isActionInProgress) && (
            <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000
            }}>
                <Spin size="large" />
            </div>
        )}
        
        {isLoading ? (
            <div className="drawer-loading-container">
            <Spin tip="Loading..." />
            </div>
        ) : (
            <div className="drawer-outer-container" style={{ marginTop: "24px" }}>
            <div className="drawer-content-container">
                <Descriptions
                bordered
                column={1}
                style={{
                    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)",
                    borderRadius: "10px",
                }}
                >
                <Descriptions.Item label="Partner" style={labelStyle}>
                    {requestData?.partner || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Type of Request" style={labelStyle}>
                    {requestData?.requestTypeDisplay || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="License" style={labelStyle}>
                    {requestData?.license || requestData?.licenseData?.name || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Name of the Party" style={labelStyle}>
                    {requestData?.partyName || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Request Date" style={labelStyle}>
                    {requestData?.requestDate
                    ? moment(requestData.requestDate).format("DD/MM/YYYY")
                    : "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Approval Date" style={labelStyle}>
                    {requestData?.approvalDate
                    ? moment(requestData.approvalDate).format("DD/MM/YYYY")
                    : "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Attachments" style={labelStyle}>
                    {renderAttachments()}
                </Descriptions.Item>
                <Descriptions.Item label="Status" style={labelStyle}>
                    <span
                    style={{
                        color:
                        requestData?.status === "APPROVED"
                            ? "limegreen"
                            : requestData?.status === "REJECTED"
                            ? "red"
                            : requestData?.status === "NEEDS REVISION"
                            ? "#f95b2c"
                            : requestData?.status === "UNDER PROCESS"
                            ? "orange"
                            : "black",
                        fontWeight: "bold"
                    }}
                    >
                    {requestData?.statusDisplay || "-"}
                    </span>
                </Descriptions.Item>

                {isUserAdmin && requestData?.workflowType && (
                <Descriptions.Item label="Workflow Type" style={labelStyle}>
                    {requestData.workflowTypeDisplay || requestData.workflowType}
                </Descriptions.Item>
                )}
                
                {/* Role-specific status displays */}
                {!isUserLegal && !isUserLicensor && requestData?.legalStatus && (
                    <Descriptions.Item label="Legal Status" style={labelStyle}>
                    <span
                        style={{
                        color:
                        requestData?.legalStatus === "APPROVED"
                            ? "limegreen"
                            : requestData?.legalStatus === "REJECTED"
                            ? "red"
                            : requestData?.legalStatus === "NEEDS REVISION"
                            ? "#f95b2c"
                            : requestData?.legalStatus === "UNDER PROCESS"
                            ? "orange"
                            : "black",
                        fontWeight: "bold"
                        }}
                    >
                        {requestData.legalStatusDisplay || requestData.legalStatus}
                    </span>
                    </Descriptions.Item>
                )}
                {!isUserLegal && !isUserLicensor && requestData?.licensorStatus && (
                    <Descriptions.Item label="Licensor Status" style={labelStyle}>
                    <span
                        style={{
                        color:
                        requestData?.licensorStatus === "APPROVED"
                            ? "limegreen"
                            : requestData?.licensorStatus === "REJECTED"
                            ? "red"
                            : requestData?.licensorStatus === "NEEDS REVISION"
                            ? "#f95b2c"
                            : requestData?.licensorStatus === "UNDER PROCESS"
                            ? "orange"
                            : "black",
                        fontWeight: "bold"
                        }}
                    >
                        {requestData.licensorStatusDisplay || requestData.licensorStatus}
                    </span>
                    </Descriptions.Item>
                )}

                {requestData?.requestDetails && (
                    <Descriptions.Item label="Further details of Request" style={labelStyle}>
                    {requestData.requestDetails}
                    </Descriptions.Item>
                )}
                {requestData?.partner_comments && (
                    <Descriptions.Item label="Partner Comments" style={labelStyle}>
                    {requestData.partner_comments}
                    </Descriptions.Item>
                )}
                {requestData?.admin_comments && (
                    <Descriptions.Item label="MPP Admin Comments" style={labelStyle}>
                    {requestData.admin_comments}
                    </Descriptions.Item>
                )}
                {requestData?.legal_comments && (
                    <Descriptions.Item label="Legal Comments" style={labelStyle}>
                    {requestData.legal_comments}
                    </Descriptions.Item>
                )}
                {requestData?.licensor_comments && (
                    <Descriptions.Item label="Licensor Comments" style={labelStyle}>
                    {requestData.licensor_comments}
                    </Descriptions.Item>
                )}
                </Descriptions>
            </div>

            {/* Action Buttons based on user role and status */}
            {(isUserAdmin || isUserLegal || isUserLicensor) && requestData && canUserAct() && (
                <div className="drawer-action-buttons-container">
                {/* Submit Button - Admin only */}
                {showSubmitButton() && (
                    <Button
                        className="gx-btn-primary approval-requests-submit-button"
                        icon={<CheckOutlined />}
                        onClick={handleAdminSubmit}
                        id={`submit-${approvalRequestId}`}
                        loading={isSubmitting}
                        disabled={isActionInProgress}
                    >
                        Submit
                    </Button>
                )}
                
                {/* Needs Revision Button - All roles */}
                {showNeedsRevisionButton() && (
                    <Button
                        className="gx-btn-yellow approval-requests-revisions-button"
                        onClick={handleNeedsRevision}
                        icon={<ExclamationCircleOutlined />}
                        id={`revisions-${approvalRequestId}`}
                        disabled={!canUserAct() || isActionInProgress}
                        loading={isRevising}
                    >
                        Needs Revision
                    </Button>
                )}

                {/* Approve Button - Admin only, not in NEEDS REVISION status */}
                {showApproveButton() && (
                    <Button
                        className="gx-btn-success approval-requests-approve-button"
                        icon={<CheckOutlined />}
                        onClick={handleApprove}
                        id={`approve-${approvalRequestId}`}
                        disabled={!canUserAct() || isActionInProgress}
                        loading={isApproving}
                    >
                        Approve
                    </Button>
                )}

                {/* Reject Button - Admin only, not in NEEDS REVISION status */}
                {showRejectButton() && (
                    <Button
                        className="gx-btn-danger approval-requests-reject-button"
                        icon={<CloseOutlined />}
                        onClick={handleReject}
                        id={`reject-${approvalRequestId}`}
                        disabled={!canUserAct() || isActionInProgress}
                        loading={isRejecting}
                    >
                        Reject
                    </Button>
                )}
                </div>
            )}
            </div>
        )}
        </Drawer>
    );
};

export default ViewApprovalRequests;